import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Button, Form, Input, Grid, Text, Icon, Card } from '@pega/cosmos-react-core';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { useForm } from 'react-hook-form';
import { ColumnProps } from '@pega/cosmos-react-core/lib/components/Table/Table';
import { StyledTable } from '../../../Utilities/custom.styled';
import { updateLoaderDetails, updateBannerInfo, setChatInfo } from '../../../../redux/actions';
import { useNavigate } from 'react-router-dom';
import { appState } from '../../../../interfaces/common';
import { updatePushNotificationUrl, addNewHeader, deleteHeader } from '../ChatApis';

interface PushNotificationFormData {
  webhook: string;
}

interface TemplateFormData {
  hKey: string;
  hValue: string;
}

interface webhookHeader {
  [key: string]: string;
}

interface SyncSDKProps {
  sdkConnectionId?: string;
  sdkWebhook?: string;
  sdkWebhookHeaders?: webhookHeader;
  pushNotificationUpdateCallback?: () => void;
}

const TemplateSection: React.FC<SyncSDKProps> = (props) => {
  const { sdkConnectionId, sdkWebhookHeaders = {}, pushNotificationUpdateCallback } = props;
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let { webhookHeaders, connectionId } = useSelector((state: appState) => state.chatInfo);
  const sessionId = useSelector((state: appState) => state.sessionId);
  const { register, handleSubmit, errors, reset, formState } = useForm<TemplateFormData>({
    mode: 'all',
  });
  if (sdkConnectionId) {
    connectionId = sdkConnectionId;
    webhookHeaders = sdkWebhookHeaders;
  }

  const onAdd = async (data: TemplateFormData) => {
    let payload: webhookHeader;
    let key = data.hKey;
    payload = { [key]: data.hValue };
    reset({ hKey: '', hValue: '' });
    dispatch(updateLoaderDetails(true));
    const json = await addNewHeader(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      if (!sdkConnectionId) dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('header_added'), 'success'));
      pushNotificationUpdateCallback && pushNotificationUpdateCallback();
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('header_addition_failed'), 'urgent'));
    }
  };

  const deleteHeaderRecord = async (key: string) => {
    let payload: webhookHeader;
    payload = { headerKey: key };
    dispatch(updateLoaderDetails(true));
    const json = await deleteHeader(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      if (!sdkConnectionId) dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('header_deleted'), 'success'));
      pushNotificationUpdateCallback && pushNotificationUpdateCallback();
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('header_delete_failed'), 'urgent'));
    }
  };

  const keyValidation = (inputval: any) => {
    const validationMsg: string = translate('valid_key_error');
    const tagPattern = /^[A-Za-z-]*$/;
    return !!tagPattern.test(inputval) ? true : validationMsg;
  };

  const columns: ColumnProps<any>[] = [
    { renderer: 'key', label: translate('header_key') },
    { renderer: 'value', label: translate('header_value') },
  ];

  let headerData = webhookHeaders
    ? Object.keys(webhookHeaders).map((item: any, index: any) => {
        return {
          id: index,
          key: <Text>{item}</Text>,
          value: (
            <>
              <Flex container={{ direction: 'row', justify: 'between', alignItems: 'center' }}>
                {webhookHeaders && <Text>{webhookHeaders[item]}</Text>}
                <Button variant="simple" icon onClick={() => deleteHeaderRecord(item)}>
                  <Icon name="trash" />
                </Button>
              </Flex>
            </>
          ),
        };
      })
    : {};

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 2,
      }}
    >
      <Text as="h2" variant="h2">
        {translate('custom_headers')}
      </Text>
      <Text>{translate('header_desc')}</Text>
      <Flex
        container={{
          direction: 'row',
          alignItems: 'start',
          itemGap: 1,
        }}
      >
        <Flex item={{ grow: 1 }}>
          <Input
            label={translate('header_key')}
            type="text"
            name="hKey"
            required={true}
            autoComplete="off"
            info={errors.hKey?.message}
            status={errors.hKey?.message ? 'error' : undefined}
            ref={register({ required: translate('key_empty_error'), validate: keyValidation })}
          />
        </Flex>
        <Flex item={{ grow: 1 }}>
          <Input
            label={translate('header_value')}
            type="text"
            name="hValue"
            required={true}
            autoComplete="off"
            info={errors.hValue?.message}
            status={errors.hValue?.message ? 'error' : undefined}
            ref={register({ required: translate('value_empty_error') })}
          />
        </Flex>
        <Flex container style={{ paddingTop: '20px' }}>
          <Button onClick={handleSubmit(onAdd)} disabled={!formState.isValid}>
            {translate('add')}
          </Button>
        </Flex>
      </Flex>
      <Card style={{ padding: 0 }}>
        <StyledTable
          hoverHighlight={true}
          padding={'default'}
          columns={columns}
          data={headerData}
          emptyContent={
            <Text variant="h5" as="h5" style={{ padding: '0.5rem 0' }}>
              {translate('no_headers_configured')}
            </Text>
          }
        />
      </Card>
    </Flex>
  );
};

const PushNotifications: React.FC<SyncSDKProps> = (props) => {
  const { sdkConnectionId, sdkWebhookHeaders, pushNotificationUpdateCallback } = props;
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const { register, handleSubmit, errors, reset } = useForm<PushNotificationFormData>({ mode: 'all' });
  const navigate = useNavigate();
  let { webhook, connectionId } = useSelector((state: appState) => state.chatInfo);
  const sessionId = useSelector((state: appState) => state.sessionId);

  if (sdkConnectionId) {
    const { sdkWebhook } = props;
    webhook = sdkWebhook;
    connectionId = sdkConnectionId;
  }

  useEffect(() => {
    const formValues = {
      webhook,
    };
    reset(formValues);
  }, [reset, webhook]);

  const actions = (
    <Flex
      container={{
        alignItems: 'start',
      }}
    >
      <Button name="Save" type="submit" variant="primary">
        {translate('save')}
      </Button>
    </Flex>
  );

  const onSave = async (data: PushNotificationFormData) => {
    dispatch(updateLoaderDetails(true));
    const json = await updatePushNotificationUrl(sessionId, connectionId, data);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      if (!sdkConnectionId) dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('notification_webhook_updated'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('notification_webhook_update_failed'), 'urgent'));
    }
  };

  const domainValidation = (url: string) => {
    const validationMsg: string = translate('domain_validation_message');

    const pattern = new RegExp(
      '^(https:\\/\\/)' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,})(/[-\\w@\\+\\.~#\\?&/=%]*)?$' + // domain
        '(\\:\\d+)?$', // port - optional
      'i',
    );

    return !!pattern.test(url) ? true : validationMsg;
  };

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 2.5,
      }}
    >
      <Form actions={actions} onSubmit={handleSubmit(onSave)}>
        <Text>{translate('webhook_url_title')}</Text>
        <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(2, 1fr)',
              gap: 2,
            }}
          >
            <Input
              label={translate('url')}
              type="text"
              name="webhook"
              required={true}
              autoComplete="off"
              ref={register({
                validate: domainValidation,
              })}
              info={errors['webhook']?.message}
              status={errors['webhook']?.message ? 'error' : undefined}
            />
          </Grid>
          <Text>
            <b>{translate('note')}:</b> {translate('sdk_push_notification_note')}
          </Text>
        </Flex>
      </Form>

      <TemplateSection
        sdkConnectionId={sdkConnectionId}
        sdkWebhookHeaders={sdkWebhookHeaders}
        pushNotificationUpdateCallback={pushNotificationUpdateCallback}
      />
    </Flex>
  );
};

export default PushNotifications;
